import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNFLLeague, NFLLeagueProvider } from '@/contexts/NFLLeagueContext';
import { useAuth } from '@/contexts/AuthContext';
import { useNFLDraftQueue } from '@/hooks/useNFLDraftQueue';
import { useNFLAutoDraft } from '@/hooks/useNFLAutoDraft';
import { useNFLDraftState, useNFLDraftSync } from '@/hooks/useNFLDraftData';
import { useDraftSabotageEffects } from '@/hooks/useDraftSabotageEffects';
import { NFL_DRAFT_CHAT, type DraftChatMessage } from '@/hooks/useDraftChat';
import DraftChatLive from '@/components/draft/DraftChatLive';
import NFLDraftSabotagePanel from '@/components/nfl/NFLDraftSabotagePanel';
import { supabase } from '@/integrations/supabase/client';
import { pauseResumeDraft, makePick, callDraftAdmin, autoDraftAll, initializeDraft, type ActionDeps } from '@/lib/nflDraftActions';
import { useDraftCue } from '@/hooks/useDraftCue';
import { cn } from '@/lib/utils';
import { Volume2, VolumeX } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import NFLDraftRoom, { CenterViewToggle } from '@/components/nfl/draftroom/NFLDraftRoom';
import CommishMenu from '@/components/nfl/draftroom/CommishMenu';
import PickTicker from '@/components/nfl/draftroom/PickTicker';
import RosterRail from '@/components/nfl/draftroom/RosterRail';
import RightRail from '@/components/nfl/draftroom/RightRail';
import AutoDraftControl from '@/components/nfl/draftroom/AutoDraftControl';
import StartDraftControl from '@/components/nfl/draftroom/StartDraftControl';
import PoolControls from '@/components/nfl/draftroom/PoolControls';
import NFLPlayerDetailModal from '@/components/nfl/NFLPlayerDetailModal';
import SabotageBanners from '@/components/nfl/draftroom/SabotageBanners';
import MobileChatPanel from '@/components/nfl/draftroom/MobileChatPanel';
import MobileDraftRoom, { type MobileTab } from '@/components/nfl/draftroom/MobileDraftRoom';
import MobileMyTeamPanel from '@/components/nfl/draftroom/MobileMyTeamPanel';
import MobileQueueSheet from '@/components/nfl/draftroom/MobileQueueSheet';
import { leaguePositionLimits, wouldExceedPositionLimit } from '@/lib/nflPositionLimits';
import { useIsMobile } from '@/hooks/use-mobile';
import { useCommissionerFeeStatus } from '@/hooks/useCommissionerFeeStatus';
import { resolveStartDraftGate } from '@/lib/nflStartDraftGate';
import BoardPanel from '@/components/nfl/draftroom/BoardPanel';
import PlayersTable from '@/components/nfl/draftroom/PlayersTable';
import type { ClockState } from '@/components/nfl/draftroom/ClockStrip';
import {
  loadCenterView, saveCenterView, loadRailTab, saveRailTab, loadHideDrafted, saveHideDrafted,
  type CenterView, type RailTab,
} from '@/lib/nflDraftRoomPrefs';
import { buildFeedEvents, filterFeed, type FeedFilter } from '@/lib/nflDraftFeed';
import { sortPlayers } from '@/lib/nflPlayerSort';
import { filterDraftPool, type PoolPositionFilter } from '@/lib/filterDraftPool';
import { deriveSabotageBanners } from '@/lib/nflSabotageBanners';
import { resolveGuessWho, isPickMasked, maskPool, MASKED_NAME } from '@/lib/nflGuessWho';
import { leagueSlotOrder } from '@/lib/nflRosterSlots';
import { useLeagueRosterability } from '@/hooks/useStartablePositions';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { excludeUnrosterable } from '@/lib/nflStartablePositions';
import { poolPositionFiltersFor } from '@/lib/filterDraftPool';
import type { DraftPickLog } from '@/types/draftPlayer';

/**
 * /nfl/draft-v2 -- REVIEW-ONLY parallel path for the draft room redesign.
 *
 * NFLDraftPage.tsx remains the LEGACY room, served at /nfl/staging and at /nfl for a
 * league whose draft is in flight (rule (e)). This page reads the SAME data
 * through the SAME hooks (useNFLDraftState + useNFLDraftSync, extracted verbatim from that
 * component), so there is no second data path and nothing here can drift from what the
 * live room sees.
 *
 * The hook call ORDER below is deliberate and must not be rearranged: state, then sabotage
 * effects (which need draftStatus), then sync (whose subscription depends on refetchActive).
 * See the header of useNFLDraftData.ts for why that cycle exists.
 *
 * READ-ONLY. Drafting, queueing from the table, sabotage play and commissioner actions are
 * intentionally not wired here -- this path is for looking at the layout against real data,
 * not for running a draft. The queue's reorder/remove ARE live, because they were already.
 */

/**
 * The room itself, with NO provider of its own.
 *
 * Exported so NFLStaging can render it inside the provider it already mounts. Nesting a
 * second NFLLeagueProvider would work but would run the whole league/team fetch twice and
 * give the two copies independent active-league state -- two sources of truth for which
 * league you are looking at, on the screen where that matters most.
 */
export interface NFLDraftRoomV2ContentProps {
  /**
   * SAB-871: how the MOBILE room leaves. Required, not optional.
   *
   * On a phone this room hides the app's bottom nav, and below 640px that bar is the only
   * tab navigation there is. The exit control in MobileDraftRoom's header is the replacement,
   * and it needs a destination the SHELL owns -- NFLStaging holds `activeTab` in state, so
   * changing the URL alone would not move the user. Typing it as required means a caller
   * cannot wire this room on mobile and forget the way out.
   */
  onExit: () => void;
}

export function NFLDraftRoomV2Content({ onExit }: NFLDraftRoomV2ContentProps) {
  const { currentLeague, currentTeam, teams, isLoading: contextLoading, refetchTeams } = useNFLLeague();

  /**
   * SAB-886: THIS league's slots, not the 21-slot constant.
   *
   * Drives both the rail's rows and its `filled / N` header. A league with no roster_slots
   * falls back to the default, so the wiped leagues (SAB-953) render exactly as before.
   */
  const rosterSlotOrder = useMemo(
    () => leagueSlotOrder(currentLeague?.settings?.roster_slots),
    [currentLeague?.settings],
  );
  const { user } = useAuth();
  const { toast } = useToast();

  const leagueId = currentLeague?.id ?? null;
  const teamId = currentTeam?.id ?? null;

  // 1. State.
  const draftData = useNFLDraftState();
  const {
    picks, draftStatus, currentPickNumber, totalPicks, onTheClockTeamId,
    timeLeft, slotByPlayerId, availablePlayers, playersLoading, isLoading,
    draftPausedAt, pauseReason, pausedRemaining,
  } = draftData;

  // 2. Sabotage effects -- needs draftStatus, returns refetchActive.
  const { activeEffects, refetchActive } = useDraftSabotageEffects({
    leagueId,
    myTeamId: teamId,
    draftStatus,
  });

  // 3. Sync -- fetchers + effects, including the picks subscription.
  const { fetchDraftState, fetchPicks, fetchAvailablePlayers } =
    useNFLDraftSync({ league: currentLeague, state: draftData, refetchActive, refetchTeams });

  const { queue: queueRaw, toggle: toggleQueue, reorder: reorderQueue, remove: removeFromQueue, reload: reloadQueue } = useNFLDraftQueue({
    leagueId,
    teamId,
    onError: message => toast({ title: 'Queue not saved', description: message, variant: 'destructive' }),
  });

  /**
   * SAB-853: the autodraft flag is REAL here now.
   *
   * This room previously passed `autoDraftEnabled={false}` hardcoded, with a handler that
   * toasted "Toggle autodraft from the live draft room" -- pointing at the legacy room,
   * which had no autodraft control either. The flag outlives the draft that set it and a
   * completed draft routes HERE (nflDraftRoomChoice), so this is the room a flagged manager
   * actually reaches once the draft is over. It has to work.
   */
  const {
    enabled: autoDraftOn, saving: autoDraftSaving, setAutoDraft,
  } = useNFLAutoDraft({
    teamId,
    refreshKey: currentPickNumber,
    onError: message => toast({ title: 'Autodraft not changed', description: message, variant: 'destructive' }),
  });

  const handleAutoDraftChange = useCallback(async (next: boolean) => {
    const applied = await setAutoDraft(next);
    if (applied === null) return; // the hook reported it and resynced
    toast({
      title: applied ? 'Autodraft on' : 'Autodraft off',
      description: applied
        ? 'Your picks will be made for you until you turn this off.'
        : 'You are picking manually again.',
    });
  }, [setAutoDraft, toast]);

  const [centerView, setCenterView] = useState<CenterView>(() => loadCenterView(leagueId));
  // SAB-932: pool controls. Search and posFilter are session state -- restoring "only QBs"
  // on reload would look like an empty pool. hideDrafted IS persisted; it is a preference
  // about how someone reads a board, not a fact about this draft.
  const [detailPlayerId, setDetailPlayerId] = useState<string | null>(null);
  const [poolQuery, setPoolQuery] = useState('');
  const [posFilter, setPosFilter] = useState<PoolPositionFilter>('ALL');
  const [hideDrafted, setHideDrafted] = useState<boolean>(() => loadHideDrafted(leagueId));
  const onHideDraftedChange = useCallback((next: boolean) => {
    setHideDrafted(next);
    saveHideDrafted(leagueId, next);
  }, [leagueId]);
  /**
   * Has this user picked a view since the draft ENDED?
   *
   * Deliberately not "since the room mounted". A click made while the draft was still
   * running is the same kind of statement as the stored preference -- it was made when the
   * Players table still had content -- and Board is specified to win over that. Counting it
   * would mean anyone who touched the toggle mid-draft never gets the completed default,
   * which is the rule inverted for exactly the users most engaged with the room.
   *
   * A ref, not state: nothing renders from it, and it must not re-trigger the effect below.
   */
  const viewTouchedAfterCompletion = useRef(false);
  const [railTab, setRailTab] = useState<RailTab>(() => loadRailTab(leagueId));
  const [feedFilter, setFeedFilter] = useState<FeedFilter>('all');
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  // Derived up here because the action handlers below close over them.
  // Same derivation the live room uses: the round of the first unfilled pick.
  const currentRound = picks.find(p => !p.player_id)?.round || 1;
  const isMyTurn = !!teamId && onTheClockTeamId === teamId;

  const [sabotagePanelOpen, setSabotagePanelOpen] = useState(false);
  const [pauseBusy, setPauseBusy] = useState(false);
  const [adminBusy, setAdminBusy] = useState(false);
  const [autoAllBusy, setAutoAllBusy] = useState(false);
  const [isPicking, setIsPicking] = useState(false);

  /**
   * SAB-871: mobile.
   *
   * `useIsMobile` is 1024px + touch, with iPad forced inside the hook -- ONE definition of
   * "phone", shared with the app nav, rather than a third breakpoint in this file.
   *
   * The TOUCH half is deliberate and is a behaviour choice worth naming: a laptop window
   * dragged narrow keeps the desktop room. That is the conservative reading -- a resized
   * desktop window is not a phone, and switching it would change what today's desktop users
   * see at widths they already use.
   *
   * Mobile tab and queue-sheet state are LOCAL and not persisted. centerView/railTab are
   * stored per league by nflDraftRoomPrefs because they survive a reload mid-draft; which
   * mobile tab you were on is a within-session position, and a stale one restored days later
   * would be worse than the default.
   */
  const isMobile = useIsMobile();
  const [mobileTab, setMobileTab] = useState<MobileTab>('players');
  const [queueSheetOpen, setQueueSheetOpen] = useState(false);
  const autoDraftCancelRef = useRef(false);

  const isCommissioner = useMemo(() => {
    if (!currentLeague || !user) return false;
    return currentLeague.commissioner_id === user.id;
  }, [currentLeague, user]);

  /**
   * Pause / resume. Ported VERBATIM from NFLDraftPage.handlePauseResume -- same endpoint,
   * same body, same refetch, same toasts.
   *
   * nfl-pause-draft itself is untouched. SAB-841's MIN_RESUME_SECONDS floor (30s, clamped
   * to the pick timer) lives inside that function and is what stops a pause taken near
   * 0:00 from stranding the draft permanently: the RPC refuses `now + 0` with BAD_DEADLINE,
   * and auto-pick skips any league carrying draft_paused_at, so nothing would ever advance
   * it again. Nothing here may bypass that path.
   */
  /** Shared wiring for every edge-function action on this page. */
  const actionDeps: ActionDeps = useMemo(() => ({
    supabaseUrl: (supabase as unknown as { supabaseUrl: string }).supabaseUrl,
    getAccessToken: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      return session?.access_token ?? null;
    },
  }), []);

  const handlePauseResume = async (action: 'pause' | 'resume') => {
    if (!currentLeague) return;
    setPauseBusy(true);
    try {
      const r = await pauseResumeDraft(currentLeague.id, action, actionDeps);
      if (!r.ok) {
        toast({ title: action === 'pause' ? 'Pause failed' : 'Resume failed', description: r.error, variant: 'destructive' });
      } else {
        toast({
          title: action === 'pause' ? 'Draft paused' : 'Draft resumed',
          description: action === 'pause'
            ? `Clock stopped with ${r.data?.remaining_seconds ?? 0}s remaining.`
            : 'Clocks are running again.',
        });
      }
      await fetchDraftState();
    } finally {
      setPauseBusy(false);
    }
  };

  /**
   * Undo / cancel. Ported from NFLDraftPage.callDraftAdmin — same endpoints, same body,
   * same toasts, same double refetch afterwards.
   *
   * CommishMenu already renders the confirmation dialogs for the two irreversible items,
   * so these run only after the user has confirmed. The live room uses window.confirm for
   * the same purpose; the dialog is the same gate in a different shape.
   */
  const runDraftAdmin = async (fn: 'nfl-undo-pick' | 'nfl-cancel-draft') => {
    if (!currentLeague) return;
    setAdminBusy(true);
    try {
      const r = await callDraftAdmin(fn, currentLeague.id, actionDeps);
      if (!r.ok) {
        toast({ title: fn === 'nfl-undo-pick' ? 'Undo failed' : 'Cancel failed', description: r.error, variant: 'destructive' });
      } else if (fn === 'nfl-undo-pick') {
        toast({ title: 'Pick undone', description: `${r.data?.player_name} removed. ${r.data?.team_name} is back on the clock.` });
      } else {
        toast({ title: 'Draft canceled', description: `${r.data?.cleared_picks} picks erased. The draft can be restarted.` });
      }
      await fetchDraftState();
      await fetchPicks();
    } finally {
      setAdminBusy(false);
    }
  };

  /**
   * SAB-927: start the draft.
   *
   * Restores the only client caller of nfl-initialize-draft, which had been stranded in
   * the legacy room's pending branch -- a branch rule (e) guarantees never renders, since
   * legacy is served only for in_progress and paused. No NFL draft could be started from
   * the UI at all between the cutover and this change.
   *
   * The gate is resolveStartDraftGate (pure, unit-tested, mutation-checked). The server
   * remains the authority; this only stops the room OFFERING an action the server would
   * refuse, and reports the refusal in the server's own order.
   */
  const [startingDraft, setStartingDraft] = useState(false);

  const commissionerFee = useCommissionerFeeStatus(currentLeague?.id);

  const startGate = resolveStartDraftGate({
    draftStatus,
    isCommissioner,
    teamCount: teams.length,
    maxTeams: currentLeague?.max_teams ?? null,
    entryFee: Number(currentLeague?.entry_fee ?? 0),
    feeRequired: commissionerFee.required,
    feeLoading: commissionerFee.loading,
    feePaid: commissionerFee.paid,
  });

  const handleStartDraft = async () => {
    if (!currentLeague || startGate.reason !== null) return;
    setStartingDraft(true);
    try {
      const r = await initializeDraft(currentLeague.id, actionDeps);
      if (!r.ok) {
        // SAB-591: the client gate already blocks this, but if the server refuses anyway
        // say the friendly sentence rather than the raw code -- verbatim from the legacy
        // handler, which is the behaviour being restored.
        const description = r.data?.code === 'COMMISSIONER_UNPAID'
          ? 'Pay your entry fee first — the league cannot start until the commissioner has paid.'
          : r.error;
        toast({ title: 'Failed to start draft', description, variant: 'destructive' });
      } else {
        toast({
          title: 'Draft started!',
          description: `${r.data?.total_picks} picks across ${r.data?.rounds} rounds.`,
        });
      }
      // Refetched on BOTH paths. A refusal can still have moved the league (the function
      // writes draft_position before it builds pick rows), and a stale room after a failed
      // start is how a commissioner ends up pressing it twice.
      await fetchDraftState();
      await fetchPicks();
    } finally {
      setStartingDraft(false);
    }
  };

  /**
   * Auto-draft every remaining pick.
   *
   * The endpoint is SERVER-CHUNKED: one call fills a batch and reports whether it is done,
   * so the client re-invokes until completion. Ported with all three of the live room's
   * loop exits intact, because each stops a different runaway:
   *   - `done`                          the draft finished
   *   - picks_made 0 WITH a stop_reason  paused / sabotage wall / no players left
   *   - guard < 100                      a backstop against an endpoint that never says done
   * Dropping any one of them turns a bounded loop into an unbounded one against the pick
   * path. The 400ms back-off on a no-reason zero is for a concurrent drafter holding the
   * lock; hammering it would make the contention worse.
   */
  const handleAutoDraftAll = async () => {
    if (!currentLeague) return;
    autoDraftCancelRef.current = false;
    setAutoAllBusy(true);
    try {
      let guard = 0;
      while (guard++ < 100) {
        if (autoDraftCancelRef.current) {
          toast({ title: 'Auto-draft stopped', description: 'The draft returns to normal on-the-clock picking.' });
          break;
        }
        const r = await autoDraftAll(currentLeague.id, actionDeps);
        if (!r.ok) {
          toast({ title: 'Auto-draft failed', description: r.error, variant: 'destructive' });
          break;
        }
        await fetchPicks();
        await fetchDraftState();
        const d = r.data ?? {};
        if (d.done) {
          toast({ title: '🏆 Draft filled', description: `All remaining picks auto-drafted (${d.total_picks} total).` });
          break;
        }
        if (d.picks_made === 0 && d.stop_reason) {
          toast({ title: 'Auto-draft stopped', description: String(d.stop_reason), variant: 'destructive' });
          break;
        }
        if (d.picks_made === 0) await new Promise(r2 => setTimeout(r2, 400));
      }
    } finally {
      setAutoAllBusy(false);
      autoDraftCancelRef.current = false;
      await fetchPicks();
      await fetchDraftState();
    }
  };

  /**
   * Make a pick. NON-OPTIMISTIC, deliberately.
   *
   * The row is not removed until the refetch returns, exactly as the live room behaves.
   * Optimistic removal is a separate change: it has to decide what the UI does for each
   * nfl_acquire_pick_lock refusal (PLAYER_ALREADY_DRAFTED, STALE_POINTER, NOT_YOUR_TURN),
   * and smuggling that decision into a port would hide it.
   *
   * commissioner_pick is `isCommissioner && !isMyTurn`, computed client-side and sent in
   * the body — byte-identical to the live room. The RPC re-validates it, and a commissioner
   * picking for someone else WITHOUT the flag is refused NOT_YOUR_TURN. Sending it
   * unconditionally would be worse: it would let a commissioner bypass their own turn check.
   */
  const handlePick = async (playerId: string) => {
    if (!currentLeague || isPicking) return;
    setIsPicking(true);
    try {
      const r = await makePick(currentLeague.id, playerId, isCommissioner && !isMyTurn, actionDeps);
      if (!r.ok) {
        toast({ title: 'Pick failed', description: r.error, variant: 'destructive' });
      } else {
        const pick = (r.data?.pick ?? {}) as Record<string, unknown>;
        toast({ title: 'Pick made!', description: `${pick.player_name} (${pick.position})` });
        await fetchPicks();
        await fetchDraftState();
        await fetchAvailablePlayers();
      }
    } finally {
      setIsPicking(false);
    }
  };

  const onCenterView = (v: CenterView) => {
    if (draftStatus === 'completed') viewTouchedAfterCompletion.current = true;
    setCenterView(v);
    saveCenterView(leagueId, v);
  };

  /**
   * A COMPLETED draft opens on the Board.
   *
   * A narrow exception to the sticky-view rule, not a repeal of it. Once the draft is over
   * the Players table is deliberately empty (the pool is not fetched), so Board is the only
   * view with anything in it, and restoring a stored `players` preference lands the user on
   * an empty screen for a draft they came back to read.
   *
   * THREE THINGS THIS MUST NOT BREAK, and the guard for each:
   *
   *  1. Stickiness DURING a draft. The condition is `=== 'completed'`, so this cannot run
   *     for in_progress or paused. The stored preference is still what loadCenterView
   *     restores on mount, untouched.
   *  2. Never auto-switching when a user comes ON THE CLOCK. Same guard -- a draft where
   *     anyone can be on the clock is by definition not completed. Note also what is NOT in
   *     the dependency list: isMyTurn, onTheClockTeamId, currentPickNumber. This effect has
   *     no way to observe the clock at all.
   *  3. A manual choice after completion. viewTouchedAfterCompletion short-circuits it, and
   *     because the ref lives for the life of the mount, one switch to Players holds for the
   *     session. A click made BEFORE completion does not count -- see the ref's own note.
   *
   * It cannot be a useState initialiser: that runs before draftStatus has been fetched, so
   * at first render every draft looks pending.
   */
  useEffect(() => {
    if (draftStatus !== 'completed') return;
    if (viewTouchedAfterCompletion.current) return;
    setCenterView('board');
  }, [draftStatus]);
  const onRailTab = (t: RailTab) => { setRailTab(t); saveRailTab(leagueId, t); };

  const teamMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const t of teams) m[t.id] = t.name;
    return m;
  }, [teams]);

  const madePicks = useMemo(() => picks.filter(p => p.player_id), [picks]);

  // Same shape the live room builds, so DraftBoard receives identical input.
  /**
   * SAB-931: Guess Who. The ONLY sabotage whose entire effect is client-side -- there is no
   * server behaviour to fall back on, so if this room does not mask, the card the attacker
   * paid for does nothing and nothing reports a failure.
   */
  const guessWho = useMemo(
    () => resolveGuessWho({
      activeEffects,
      myTeamId: currentTeam?.id ?? null,
      currentPickNumber,
      picksPerRound: teams.length,
    }),
    [activeEffects, currentTeam?.id, currentPickNumber, teams.length],
  );

  const pickLogs = useMemo<DraftPickLog[]>(() => madePicks.map(p => ({
    pickNumber: p.pick_number,
    round: p.round,
    pickInRound: p.pick_in_round,
    teamId: p.team_id,
    teamName: teamMap[p.team_id] || 'Unknown',
    playerId: p.player_id as string,
    // SAB-931: picks at or after the mask began are hidden; earlier ones stay visible --
    // you cannot unsee what was already public, and the point is hiding what comes next.
    playerName: isPickMasked(guessWho, p.pick_number) ? MASKED_NAME : (p.player_name || 'Unknown'),
    position: p.player_position || '?',
    timestamp: new Date(),
    wasAutoPick: p.auto_picked,
    wasCommissionerPick: p.picked_by_commissioner,
    nflTeam: p.player_nfl_team,
    byeWeek: p.player_bye_week,
  })), [madePicks, teamMap, guessWho]);

  // SAB-845: while reverse_order or skip_pick is live, nfl_draft_picks still holds BASE
  // SNAKE ORDER and only the current row is rewritten at pick time. Every unpicked cell and
  // every ticker card would therefore name the wrong team. Same predicate the live room uses.
  /**
   * SAB-931: the sabotage banners. Five of seven effects had no UI in this room at all;
   * three of those are server-enforced, so they were happening with no explanation.
   * `picksPerRound` is the team count -- that is what converts a round expiry into the
   * pick countdown a manager on the clock actually thinks in.
   */
  const sabotageBanners = useMemo(
    () => deriveSabotageBanners({
      activeEffects,
      myTeamId: currentTeam?.id ?? null,
      currentPickNumber,
      picksPerRound: teams.length,
    }),
    [activeEffects, currentTeam?.id, currentPickNumber, teams.length],
  );

  /**
   * SAB-931: slam the detail modal shut when a mask lands.
   *
   * Withholding onPlayerClick stops a masked player being OPENED, but says nothing about
   * one already on screen. Sabotage arrives over realtime mid-draft, so "open Bijan's card,
   * opponent plays Guess Who" would otherwise leave the full record -- name, position,
   * injury, game log -- sitting open in front of exactly the viewer the card was bought to
   * blind. The spec is "sees ONLY player headshots"; an open modal violates that however it
   * got there.
   */
  useEffect(() => {
    if (guessWho.active) setDetailPlayerId(null);
  }, [guessWho.active]);

  const orderScrambled = useMemo(
    () => (activeEffects ?? []).some(
      (e: { active?: boolean; effect_type?: string }) =>
        e.active && (e.effect_type === 'reverse_order' || e.effect_type === 'skip_pick'),
    ),
    [activeEffects],
  );

  // SAB-992: which teams are on autodraft RIGHT NOW, from nfl_teams.auto_draft_enabled.
  //
  // This used to be derived from picks[].auto_picked -- "this team has had at least one
  // server-made pick". That is a HISTORICAL LATCH, and it is why the marker did not track
  // the toggle: turning autodraft off left it lit forever, and turning it on showed
  // nothing until the team had actually been auto-picked for. No realtime subscription
  // could have fixed that, because the flag was never the source.
  //
  // The ticker shows UPCOMING picks, so the question it answers is "will this team be
  // picked for?", which is a property of the flag, not of pick history. The FEED keeps its
  // historical per-pick AUTO marker (RightRail, `e.auto`) -- that one is a fact about a
  // pick that already happened and must not move.
  const autoTeams = useMemo(
    () => new Set(teams.filter(t => t.auto_draft_enabled === true).map(t => t.id)),
    [teams],
  );

  const totalRounds = useMemo(
    () => (picks.length > 0 ? Math.max(...picks.map(p => p.round)) : 0),
    [picks],
  );

  const draftOrder = useMemo(
    () => picks.map(p => ({
      id: p.id, team_id: p.team_id, round: p.round,
      pick_number: p.pick_number, round_pick: p.pick_in_round,
    })),
    [picks],
  );

  // Ticker: the run around the current pick.
  const tickerEntries = useMemo(() => {
    const from = Math.max(0, currentPickNumber - 2);
    return picks.slice(from, from + 12).map(p => ({
      pickNumber: p.pick_number,
      round: p.round,
      pickInRound: p.pick_in_round,
      teamId: p.team_id,
      teamName: teamMap[p.team_id] || 'Unknown',
    }));
  }, [picks, currentPickNumber, teamMap]);

  const feed = useMemo(() => filterFeed(buildFeedEvents({
    picks: madePicks.map(p => ({
      pick_number: p.pick_number, round: p.round, team_id: p.team_id,
      player_id: p.player_id,
      player_name: isPickMasked(guessWho, p.pick_number) ? MASKED_NAME : p.player_name,
      position: p.player_position,
      // The extracted hook does not select picked_at, so ordering falls back to pick order.
      picked_at: new Date(2000, 0, 1, 0, 0, p.pick_number).toISOString(),
      auto_picked: p.auto_picked,
    })),
    teamNames: teamMap,
    myTeamId: teamId,
  }), feedFilter), [madePicks, teamMap, teamId, feedFilter, guessWho]);

  const draftedLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of madePicks) {
      if (p.player_id) {
        m.set(p.player_id, `${p.round}.${String(p.pick_in_round).padStart(2, '0')} · ${teamMap[p.team_id] || 'Unknown'}`);
      }
    }
    return m;
  }, [madePicks, teamMap]);

  /**
   * SAB-996 item 1: the queue must react to PICKS, not only to page load.
   *
   * SAB-991 fixed the server side -- nfl_draft_queues rows are deleted when a player is
   * drafted -- but the client loaded the queue once on mount and never re-read it, so a
   * drafted player sat in the sidebar until refresh. Adam saw his own queue entry survive
   * a pick he made HIMSELF, which is what rules out the trigger and points here.
   *
   * Two mechanisms, deliberately both:
   *
   *   - `queue` filters drafted ids out on the spot, so the row disappears the instant the
   *     pick lands rather than after a round trip. During a live draft the difference
   *     between "immediately" and "after a fetch" is the difference between trusting the
   *     sidebar and not.
   *   - the effect re-reads from the server, which is authoritative and also catches
   *     anything the local `picks` view has not seen.
   *
   * Filtering alone would be wrong: the row would vanish locally while the DB row lived on
   * if the trigger ever regressed, and nfl-auto-pick reads the DB, not this component.
   */
  const queue = useMemo(
    () => queueRaw.filter(pid => !draftedLabel.has(pid)),
    [queueRaw, draftedLabel],
  );

  useEffect(() => {
    reloadQueue();
    // Keyed on the PICK COUNT, not the picks array: `picks` is rebuilt by every fetch and
    // depending on its identity would reload the queue on a timer tick.
  }, [picks.length, reloadQueue]);


  /**
   * SAB-932: the pool the centre panel actually shows.
   *
   * Mapped to the row shape first, then filtered through the SHARED helper -- the same one
   * the legacy room calls, so while both rooms are live (SAB-931) they cannot disagree
   * about what "available" means. The helper carries the reconciliation of SAB-843 (a
   * hunted player must never silently vanish) with SAB-932 (browsing must not be a wall of
   * unpickable rows): drafted are hidden while BROWSING, always shown while SEARCHING.
   *
   * draftedIds comes from draftedLabel, which is already derived from `picks` -- no query,
   * no column, no new fetch.
   */
  // SAB-955: positions this league can start. Applied to the RAW pool below, BEFORE
  // masking -- maskPoolRow rewrites `position` to "???", so filtering after it would empty
  // the whole pool while a Guess Who card is active.
  // SAB-998: same hook as both players surfaces; one eligibility read serves all of it.
  const { startable, hasFlex, flex, idp } = useLeagueRosterability(currentLeague?.settings?.roster_slots);

  /**
   * SAB-989: which positions the pool should grey out, and why.
   *
   * ── KEYED ON THE TEAM THAT WOULD RECEIVE THE PLAYER ─────────────────────
   *
   * Not on the viewer. A commissioner may pick OUT OF TURN (`isMyTurn || isCommissioner`
   * below, handlePick sends commissioner_pick), and that pick goes to the team on the
   * clock, not to the commissioner's own team. Greying against the viewer's roster would
   * then refuse the wrong rows and permit the ones the database is about to reject.
   *
   * The team on the clock is also what guard_nfl_position_limit counts, because
   * nfl_acquire_pick_lock inserts the roster row with p_effective_team_id -- so this and
   * the trigger answer the same question about the same roster.
   *
   * When that team is NOT the viewer's, the reason names it. A bare "Roster limit reached"
   * on someone else's roster reads as a statement about your own, which is the kind of
   * quiet untruth the SAB-986 denominators were.
   *
   * Mid-draft a team's roster IS its made picks, so `picks` is the same set the trigger
   * counts in nfl_rosters.
   */
  const capTeamId = onTheClockTeamId ?? teamId;
  const cappedPositions = useMemo(() => {
    const out = new Map<string, string>();
    const caps = leaguePositionLimits((currentLeague?.settings as any)?.position_limits);
    if (!capTeamId || Object.keys(caps).length === 0) return out;
    const held = picks
      .filter(p => p.team_id === capTeamId && p.player_id && p.player_position)
      .map(p => p.player_position as string);
    const isMine = capTeamId === teamId;
    for (const pos of Object.keys(caps)) {
      const chk = wouldExceedPositionLimit(caps, held, pos);
      if (!chk.blocked || !chk.reason) continue;
      out.set(pos, isMine ? chk.reason : `${teamMap[capTeamId] ?? 'That team'} — ${chk.reason}`);
    }
    return out;
  }, [currentLeague?.settings, picks, capTeamId, teamId, teamMap]);
  const positionFilters = useMemo(
    () => poolPositionFiltersFor(startable, { hasFlex, flex, idp }),
    [startable, hasFlex, flex, idp],
  );

  const mappedRows = useMemo(
    () => maskPool(sortPlayers(excludeUnrosterable(availablePlayers, startable).map(p => ({
      id: p.id, full_name: p.full_name, position: p.position,
      nfl_team: p.franchise?.alias ?? null,
      // SAB-813: these are TWO signals and they now travel as two fields. This line used to
      // read `injury_status: p.status`, which put the roster state into a field named after
      // the weekly injury report. Nothing was wrong on screen -- there was one pill and one
      // source -- but the name lied, and the next reader would have had no way to tell that
      // masking `injury_status` was covering `status`. Both are masked now; see maskPoolRow.
      injury_status: p.injury_status ?? null,
      status: p.status ?? null,
      proj_sab_pts: p.proj_sab_pts, adp: p.adp,
      bye_week: p.franchise?.bye_week_2026 ?? null,
      // SAB-931: the face is the whole of Guess Who. maskPoolRow deliberately leaves it
      // alone; everything else identifying is stripped.
      headshot_url: p.headshot_url ?? null,
    })), 'trash'), guessWho),
    // MASK, THEN FILTER. filterDraftPool matches over these rows, so a masked player's real
    // name is not present as matchable text and search cannot return them -- with no second
    // "is this hidden" rule for search to keep in sync with the renderer. Reversing the
    // order would search real names and render fake ones.
    [availablePlayers, guessWho],
  );

  /*
   * SAB-959: 150ms debounce, per README "Interactions & Behavior".
   *
   * The INPUT keeps `poolQuery` so typing never lags; only the filter over ~2,200 players
   * waits. Clearing resolves immediately -- getting the full pool back, often on a clock,
   * must not feel like the clear button missed.
   */
  const debouncedPoolQuery = useDebouncedValue(poolQuery, 150, (q) => q.trim() === '');

  const pool = useMemo(() => filterDraftPool({
    players: mappedRows,
    draftedIds: new Set(draftedLabel.keys()),
    query: debouncedPoolQuery,
    posFilter,
    hideDrafted,
    // SAB-998: only consulted when posFilter is FLEX.
    flexPositions: flex,
    // SAB-812: only consulted when posFilter is IDP.
    idpPositions: idp,
  }), [mappedRows, draftedLabel, debouncedPoolQuery, posFilter, hideDrafted, flex, idp]);

  const tableRows = pool.rows;

  const chatAuthorByUserId = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of teams) if (t.owner_id) m.set(t.owner_id, t.name);
    return m;
  }, [teams]);

  /**
   * SAB-918: DRAFT ORDER, from the field that means it.
   *
   * The context query orders by `wins`, and pre-draft every team has zero — a sort key
   * that is entirely tied. Postgres gives no stable-sort guarantee there, so the array
   * arrives in an arbitrary order that changes between runs and plans. The board rendered
   * that order as the draft order, and the rail printed the ARRAY INDEX as "Slot N", so a
   * commissioner at position 6 was shown as slot 1. A wrong printed number is an assertion
   * of fact, not a cosmetic ordering, which is why it is fixed here rather than tolerated.
   *
   * Same defect as SAB-782: a tied sort key with no deterministic secondary. The ordering
   * below is TOTAL, for the same reason nfl_league_standings ends on team_id --
   *
   *   draft_position ascending, NULLS LAST  (unassigned teams sort after assigned ones)
   *   then id                               (deterministic, so a partially-assigned or
   *                                          wholly-unassigned league is still stable
   *                                          rather than arbitrary)
   *
   * The DRAFT ITSELF never depended on any of this: nfl-initialize-draft sorts by
   * draft_position independently when building the pick rows. This only fixes what is
   * shown before the draft starts.
   */
  const orderedTeams = useMemo(() => {
    return [...teams].sort((a, b) => {
      const ap = a.draft_position, bp = b.draft_position;
      if (ap != null && bp != null && ap !== bp) return ap - bp;
      if (ap != null && bp == null) return -1;   // assigned before unassigned
      if (ap == null && bp != null) return 1;
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
  }, [teams]);

  const effectiveTeamId = selectedTeamId ?? teamId ?? orderedTeams[0]?.id ?? null;

  const railTeams = useMemo(() => orderedTeams.map(t => ({
    id: t.id,
    name: t.name,
    logoUrl: t.logo_url,
    // SAB-918: the team's OWN draft_position, never the array index. Null when no order
    // has been assigned yet -- the rail omits the slot rather than inventing one, because
    // "Slot 1" for a team with no assigned slot is worse than showing nothing.
    draftSlot: t.draft_position ?? null,
    rosterCount: madePicks.filter(p => p.team_id === t.id).length,
    isMine: t.id === teamId,
    isOnTheClock: t.id === onTheClockTeamId,
  })), [orderedTeams, teamId, onTheClockTeamId, madePicks]);

  // SAB-842/739: slot comes from nfl_rosters.position_slot via the extracted hook. Never
  // re-derived from position here -- FLEX matches nothing and every IDP variant would fall
  // through to the bench.
  const rosterPlayerIds = useMemo(
    () => madePicks.filter(p => p.team_id === effectiveTeamId).map(p => p.player_id as string),
    [madePicks, effectiveTeamId],
  );

  const playersById = useMemo(() => {
    const m = new Map<string, { id: string; full_name: string; position: string; nfl_team?: string | null; pickLabel?: string | null }>();
    for (const p of madePicks) {
      if (!p.player_id) continue;
      m.set(p.player_id, {
        id: p.player_id,
        full_name: p.player_name || 'Unknown',
        position: p.player_position || '?',
        nfl_team: p.player_nfl_team,
        pickLabel: `${p.round}.${String(p.pick_in_round).padStart(2, '0')}`,
      });
    }
    return m;
  }, [madePicks]);


  // Paused if EITHER signal says so. draft_status is what the sanctioned pause path sets,
  // settings.draft_paused_at is what the server actually obeys when deciding not to
  // advance. Keying on draft_status alone rendered Queue Proof as LIVE with a red 0:00
  // countdown while auto-pick was skipping it entirely.
  //
  // GATED ON A RUNNING DRAFT. draft_paused_at is not cleared when a draft completes --
  // nfl_advance_draft_pointer's completion UPDATE strips draft_pick_deadline but not the
  // pause keys, and nfl_acquire_pick_lock does not read draft_paused_at at all, so the
  // SAB-534 divergent state (draft_status 'in_progress' WITH draft_paused_at set, which two
  // staging leagues are in right now) can be picked to completion. That league would then
  // render PAUSED BY COMMISSIONER with a frozen clock on a finished draft, forever.
  const draftRunning = draftStatus === 'in_progress' || draftStatus === 'paused';
  const isPaused = draftRunning && (draftStatus === 'paused' || !!draftPausedAt);

  // Paused outranks everything, including "your turn" and the sub-15s urgency colour: a
  // countdown on a stopped draft tells a manager to hurry for a pick they cannot take.
  // No live draft => idle. Falling through to 'running' made a completed draft show
  // "COMPLETED" and the status word "ON CLOCK" at the same time.
  const clockState: ClockState =
    !draftRunning ? 'idle'
    : isPaused ? 'paused'
    : isMyTurn ? 'yours'
    : timeLeft !== null && timeLeft <= 15 ? 'urgent'
    : 'running';

  // While paused, show the captured remaining rather than counting a stale deadline down.
  // The deadline is NULLed by the sanctioned path, but survives in the inconsistent state.
  const displayTime = isPaused ? pausedRemaining : timeLeft;

  /**
   * SAB-988 part 1: the in-room audio cue.
   *
   * Enabled only while the draft is actually running -- a completed or pending draft has no
   * clock to warn about, and a cue there would be noise on a page nobody is drafting from.
   */
  const cue = useDraftCue({
    isMyTurn,
    pickNumber: currentPickNumber,
    secondsLeft: displayTime,
    isPaused,
    enabled: draftRunning,
  });

  /**
   * Browsers refuse to play audio that no gesture asked for, and a suspended AudioContext
   * drops every play() SILENTLY. So the first interaction anywhere in the room unlocks it.
   * Capture phase, once: it must not depend on the user happening to click a particular
   * control, and it must not keep running for the rest of the draft.
   */
  useEffect(() => {
    if (!draftRunning) return;
    const onGesture = () => cue.arm();
    window.addEventListener('pointerdown', onGesture, { once: true, capture: true });
    window.addEventListener('keydown', onGesture, { once: true, capture: true });
    return () => {
      window.removeEventListener('pointerdown', onGesture, { capture: true });
      window.removeEventListener('keydown', onGesture, { capture: true });
    };
  }, [draftRunning, cue]);

  const pauseWord =
    pauseReason === 'commissioner' ? 'PAUSED BY COMMISSIONER'
    : pauseReason === 'overnight' ? 'PAUSED — OVERNIGHT'
    : pauseReason === 'inactivity' ? 'PAUSED — INACTIVITY'
    : 'PAUSED';

  /**
   * SAB-871: the mobile action bar's three derived inputs. All from state that already
   * exists here -- nothing new is fetched for mobile.
   */
  // The head of the queue, resolved through the same pool the desktop rail resolves against,
  // so mobile shows the same name (or the same fallback) as the rail does.
  const topOfQueue = useMemo(() => {
    const pid = queue[0];
    if (!pid) return null;
    const p = availablePlayers.find(a => a.id === pid);
    return { name: p?.full_name ?? `Queued player · ${pid.slice(0, 8)}`, position: p?.position ?? '—' };
  }, [queue, availablePlayers]);

  // How many picks until this team is up. Counted off the SAME draftOrder the board renders,
  // so it cannot disagree with what the user is looking at. Null team, or no upcoming pick,
  // yields 0 rather than a guess.
  const picksAway = useMemo(() => {
    if (!teamId) return 0;
    const next = picks.find(p => p.team_id === teamId && p.pick_number >= currentPickNumber);
    if (!next) return 0;
    return Math.max(0, next.pick_number - currentPickNumber);
  }, [picks, teamId, currentPickNumber]);

  // The primary action is the same one the desktop Draft button fires, on the same guard.
  const onMobilePrimaryAction = () => {
    const pid = queue[0];
    if (pid) void handlePick(pid);
  };

  if (contextLoading) return <div className="p-8 text-sm text-muted-foreground">Loading league…</div>;
  if (!currentLeague) {
    return (
      <div className="p-8 text-sm text-muted-foreground">
        No active NFL league. Pick one from the league switcher, then return to /nfl/draft-v2.
      </div>
    );
  }

  const onClockName = onTheClockTeamId ? (teamMap[onTheClockTeamId] ?? 'Unknown') : null;

  /**
   * SAB-871: hoisted so the MOBILE and DESKTOP rooms render from the SAME derivations.
   * Copying these into the mobile branch would have let the two rooms disagree about what
   * the clock says or what an empty pool means -- the exact drift the shared-panel reuse
   * below is avoiding.
   */
  /*
   * SAB-959: ON THE CLOCK is its own eyebrow, per README "Interactions & Behavior"
   * ("PENDING / Waiting" -> "ON THE CLOCK / <prompt>" when it is the user's pick).
   *
   * Ordered so PAUSED still outranks it, for the same reason clockState does: a countdown
   * on a stopped draft tells a manager to hurry for a pick they cannot take.
   *
   * Changed in the SHARED derivation rather than in the mobile header, so the desktop
   * ClockStrip says the same thing. Colouring only the mobile eyebrow orange would have
   * been the smaller diff and would have left the two rooms describing the same moment
   * differently -- the drift this block was hoisted to prevent.
   */
  const clockEyebrow =
    isPaused ? pauseWord
    : isMyTurn && draftRunning ? 'ON THE CLOCK'
    : draftStatus === 'in_progress' ? 'LIVE'
    : (draftStatus ?? '').toUpperCase() || 'DRAFT';
  const clockHeadline =
    isPaused ? 'Draft paused'
    // "Your pick" rather than "You are on the clock": the eyebrow now carries "on the
    // clock", and repeating it wastes the 120px the title actually has. This is also the
    // wording ClockStrip's own prop comment already documented for this case.
    : isMyTurn ? 'Your pick'
    : onClockName ? `${onClockName} is on the clock`
    : draftStatus === 'completed' ? 'Draft complete' : 'Waiting';
  // draft_current_pick is total+1 once the draft finishes, so the raw value reads
  // "Pick 153 of 152". Clamp it and say the draft is done instead.
  const clockSubline =
    draftStatus === 'completed'
      ? `Draft complete · ${madePicks.length} picks`
      : totalPicks > 0
        ? `Round ${picks.find(p => p.pick_number === currentPickNumber)?.round ?? '—'} · Pick ${Math.min(currentPickNumber, totalPicks)} of ${totalPicks}`
        : `${madePicks.length} picks made`;
  const boardTeams = orderedTeams.map(t => ({ id: t.id, name: t.name }));
  /**
   * The players-table empty state, for BOTH rooms.
   *
   * SAB-927: this used to be two divergent copies. Mobile read this const, which handled
   * only 'completed', so a pending draft on a phone rendered a bare empty table with no
   * explanation at all. Desktop had its own inline ternary that DID handle pending. One
   * definition now, consumed by both, so the next edit cannot land on one room only.
   *
   * The pending copy previously read "Players load when the commissioner starts the
   * draft" -- which, for the three days the start control was unreachable, instructed
   * every reader to press a button that did not exist anywhere in the room. It now names
   * where the control actually is, and says something different to the person who has it
   * than to the people waiting on them.
   */
  const noMatches = pool.matchedCount === 0 && mappedRows.length > 0;

  const playersEmptyState =
    // SAB-932: a filter that matched nothing is NOT the same as an unfetched pool, and
    // saying "the draft has not started" to someone who just mistyped a name would be a
    // false statement about the draft. Checked FIRST so it wins over the status copy.
    noMatches
      ? {
          title: 'No players match',
          body: poolQuery.trim()
            ? `Nothing matches "${poolQuery.trim()}"${posFilter !== 'ALL' ? ` in ${posFilter}` : ''}. Drafted players are always shown when you search, so this is not the hide filter.`
            // SAB-959: names no control. Mobile replaced HIDE DRAFTED with an
            // ALL/AVAILABLE segment while desktop still has the toggle, so copy that
            // named either one would be a false instruction in the other room. Says what
            // is true everywhere -- that drafted players are being hidden -- and lets the
            // control the reader can actually see speak for itself.
            : hideDrafted
              // SAB-959, README empty state. Names the POSITION, because that is what the
              // reader just filtered to, and offers the way out as an action rather than
              // an instruction -- copy naming a control would be wrong in one of the two
              // rooms, which is what the previous "turn off HIDE DRAFTED" wording was.
              ? posFilter === 'ALL'
                ? 'Every remaining player has already been drafted.'
                : `Nobody left at ${posFilter}.`
              : `No ${posFilter} players are available.`,
          // Only when the hide is what emptied the list. Offering "Show all" to someone
          // who is already showing all would be a button that does nothing.
          action: hideDrafted && !poolQuery.trim()
            ? { label: 'SHOW ALL', onClick: () => onHideDraftedChange(false) }
            : undefined,
        }
      : draftStatus === 'completed'
      ? {
          title: 'Draft is complete!',
          body: 'This draft is over, so there is nothing left to draft here. The Board has every pick that was made, and the roster rail on the left has each team\u2019s final squad. To add or drop players, use your team page.',
        }
      : startGate.startable
        ? {
            title: 'The draft has not started',
            body: startGate.showControl
              ? 'Use Start Draft in the toolbar above to kick it off. The player pool loads once the draft is running.'
              : 'Waiting for the commissioner to start the draft. The player pool loads once it is running.',
          }
        : undefined;

  /**
   * SAB-871: THE MOBILE SWITCH.
   *
   * Below 1024px on a touch device the room is a different room, not a squeezed one. The
   * desktop grid gives a phone a 68px centre column against a fixed 322px rail -- 83% of the
   * screen to a rail -- and no amount of shrinking fixes that, so the panels are shown ONE
   * AT A TIME behind a four-item tab bar instead.
   *
   * Everything below is the SAME state the desktop room renders from: same picks, same
   * queue, same pool, same handlers. Only the arrangement differs. Nothing here fetches.
   */
  if (isMobile) {
    return (
      <>
        <MobileDraftRoom
          timeLeft={displayTime}
          clockState={clockState}
          clockEyebrow={clockEyebrow}
          clockHeadline={clockHeadline}
          clockSubline={clockSubline}
          /*
            SAB-959: autodraft moved out of the panel toolbar and into the header as a pill.

            ⚠️ STILL NOT GATED ON mobileTab, which is the property SAB-881 and SAB-953 both
            turned on: a standing delegation of every remaining pick must not sit behind a
            tab. The header is not tab-gated at all, so the move strengthens that rather
            than risking it. Gated only on `draftStatus !== 'completed'`, exactly as the
            desktop toolbar is.
          */
          autoDraftPill={draftStatus !== 'completed' ? (
            <AutoDraftControl
              variant="pill"
              enabled={autoDraftOn}
              saving={autoDraftSaving}
              onChange={handleAutoDraftChange}
            />
          ) : undefined}
          // null = unknown, same as desktop; the sabotage panel fetches its own cards.
          sabotageCount={null}
          onSabotageClick={() => setSabotagePanelOpen(true)}
          tab={mobileTab}
          onTabChange={setMobileTab}
          /* SAB-931 item 4: the SAME menu the desktop room gets, same handlers, same
             confirmation dialogs. A commissioner on a phone had none of these mid-draft. */
          commishMenu={
            <CommishMenu
              isCommissioner={isCommissioner}
              isPaused={isPaused}
              draftStatus={draftStatus}
              hasMadePicks={madePicks.length > 0}
              onPauseResume={handlePauseResume}
              busy={pauseBusy || adminBusy || autoAllBusy}
              onUndoLastPick={() => runDraftAdmin('nfl-undo-pick')}
              onCancelDraft={() => runDraftAdmin('nfl-cancel-draft')}
              onAutoDraftAll={handleAutoDraftAll}
            />
          }
          // Parity with the desktop rail, which also passes 0. Inventing an unread count
          // here would make mobile claim something the rest of the room does not track.
          unreadChat={0}
          onExit={onExit}
          miniTicker={
            <>
              {/* SAB-931: sabotage banners ABOVE the ticker -- the ticker is glanceable and
                  the banners are not, so burying them under it would put the thing that
                  needs reading beneath the thing that needs scanning. */}
              <SabotageBanners banners={sabotageBanners} className="mb-2" />
              <PickTicker
                entries={tickerEntries}
                currentPick={currentPickNumber}
                myTeamId={teamId}
                autopickTeamIds={autoTeams}
                orderScrambled={orderScrambled}
              />
            </>
          }
          /*
            SAB-927: the mobile half of the start control.

            panelToolbar is the mobile twin of the desktop centreToolbar -- it renders
            above the panel and OUTSIDE the tab switch, so the control is reachable from
            every tab, not just PLAYERS. The slot already existed and had never been
            passed; a commissioner on a phone had no way to start a draft at all.
          */
          panelToolbar={
            (startGate.showControl || draftStatus !== 'completed')
              ? (
                <div className="flex flex-col gap-2">
                  {/* Single controls share a wrapping row; the pool controls get their own
                      line below because they are a group, not one control. */}
                  <div className="flex flex-wrap items-center gap-2">
                    {startGate.showControl && (
                      <StartDraftControl
                        reason={startGate.reason}
                        starting={startingDraft}
                        onStart={handleStartDraft}
                      />
                    )}
                  </div>
                  {/* SAB-932: same controls on mobile. Only on the PLAYERS tab -- they
                      filter nothing on BOARD, MY TEAM or CHAT, and the toolbar is the
                      scarcest space in the room. */}
                  {mobileTab === 'players' && draftStatus !== 'completed' && (
                    <PoolControls
                      layout="stacked"
                      positionFilters={positionFilters}
                  query={poolQuery}
                  onQueryChange={setPoolQuery}
                  posFilter={posFilter}
                  onPosFilterChange={setPosFilter}
                  hideDrafted={hideDrafted}
                  onHideDraftedChange={onHideDraftedChange}
                  hiddenDraftedCount={pool.hiddenDraftedCount}
                />
                  )}
                </div>
              )
              : undefined}
          // Matches what PlayersTable gets: a commissioner may pick out of turn, and
          // handlePick sends commissioner_pick so the RPC accepts it.
          isMyTurn={isMyTurn || isCommissioner}
          // SAB-885: ...but that stays true forever on a finished draft, which is how the
          // bar came to offer "Pick a player" on a draft with nothing left to draft. The
          // desktop room has no equivalent defect because PlayersTable already swaps its
          // draft buttons for the "Draft is complete!" empty state.
          draftComplete={draftStatus === 'completed'}
          topOfQueue={topOfQueue}
          picksAway={picksAway}
          onPrimaryAction={onMobilePrimaryAction}
          onQueueButton={() => setQueueSheetOpen(true)}
          drafting={isPicking}
          panel={
            mobileTab === 'players' ? (
              <PlayersTable
                players={tableRows}
                loading={playersLoading}
                draftedLabel={draftedLabel}
                cappedPositions={cappedPositions}
                queuedIds={new Set(queue)}
                onToggleQueue={toggleQueue}
                onDraft={handlePick}
                isMyTurn={isMyTurn || isCommissioner}
                drafting={isPicking}
                emptyState={playersEmptyState}
                onPlayerClick={guessWho.active ? undefined : setDetailPlayerId}
              />
            ) : mobileTab === 'board' ? (
              <BoardPanel
                pickLogs={pickLogs}
                teams={boardTeams}
                totalRounds={totalRounds}
                currentPick={currentPickNumber}
                draftOrder={draftOrder}
                orderScrambled={orderScrambled}
              />
            ) : mobileTab === 'myteam' ? (
              <MobileMyTeamPanel
                slotOrder={rosterSlotOrder}
                slotAssignments={slotByPlayerId}
                playersById={playersById}
                rosterPlayerIds={rosterPlayerIds}
                teamName={currentTeam?.name ?? null}
              />
            ) : (
              <MobileChatPanel
                feed={feed}
                feedFilter={feedFilter}
                onFeedFilterChange={setFeedFilter}
                chatPanel={
                  <DraftChatLive
                    draftId={leagueId}
                    teamName={currentTeam?.name ?? null}
                    source={NFL_DRAFT_CHAT}
                    resolveAuthorName={(msg: DraftChatMessage) =>
                      chatAuthorByUserId.get(msg.user_id) ?? 'Manager'}
                  />
                }
              />
            )
          }
        />

        {/* The action bar's queue button. A sheet rather than a fifth tab. */}
        <MobileQueueSheet
          open={queueSheetOpen}
          onOpenChange={setQueueSheetOpen}
          queue={queue.map(pid => {
            const p = availablePlayers.find(a => a.id === pid);
            return {
              playerId: pid,
              name: p?.full_name ?? `Queued player · ${pid.slice(0, 8)}`,
              position: p?.position ?? '—',
              projection: p?.proj_sab_pts ?? null,
            };
          })}
          onQueueMove={reorderQueue}
          onQueueRemove={removeFromQueue}
        />

        {currentLeague && (
          <NFLDraftSabotagePanel
            open={sabotagePanelOpen}
            onOpenChange={setSabotagePanelOpen}
            leagueId={currentLeague.id}
            myTeamId={currentTeam?.id ?? null}
            myUserId={user?.id ?? null}
            teams={boardTeams}
            currentRound={currentRound}
            onPlayed={() => { refetchActive(); }}
          />
        )}

        {/* SAB-931: player detail.
            MOUNTED ONLY WHEN OPEN. The legacy room mounts it unconditionally, and its
            closed-state effect still runs four setState calls -- two returning fresh
            identities -- on every render of the room. Cheaper and simpler to not mount it,
            and it removes a whole class of interference with the live-draft render path.
            detailPlayerId is the only thing that opens it, the room refuses to set it while
            masked, and the effect above clears it if a mask lands while it is open. */}
        {detailPlayerId && (
          <NFLPlayerDetailModal
            playerId={detailPlayerId}
            open
            onOpenChange={(open) => { if (!open) setDetailPlayerId(null); }}
          />
        )}
      </>
    );
  }


  return (
    <>
      {/* The PREVIEW banner is gone: this room is now canonical at /nfl and its actions are
          wired, so telling users it is read-only would be false. */}
      <NFLDraftRoom
        topNavRight={
          <CommishMenu
            isCommissioner={isCommissioner}
            isPaused={isPaused}
            draftStatus={draftStatus}
            hasMadePicks={madePicks.length > 0}
            onPauseResume={handlePauseResume}
            busy={pauseBusy || adminBusy || autoAllBusy}
            onUndoLastPick={() => runDraftAdmin('nfl-undo-pick')}
            onCancelDraft={() => runDraftAdmin('nfl-cancel-draft')}
            onAutoDraftAll={handleAutoDraftAll}
          />
        }
        leagueName={currentLeague.name}
        timeLeft={displayTime}
        clockState={clockState}
        clockEyebrow={clockEyebrow}
        clockHeadline={clockHeadline}
        clockSubline={clockSubline}
        // null = unknown. NFLDraftSabotagePanel fetches nfl_sabotage_cards itself.
        sabotageCount={null}
        onSabotageClick={() => setSabotagePanelOpen(true)}
        centerView={centerView}
        onCenterViewChange={onCenterView}
        ticker={
          <>
            {/* SAB-931: see the mobile note -- same reasoning, same placement. */}
            <SabotageBanners banners={sabotageBanners} className="mb-2" />
            <PickTicker
              entries={tickerEntries}
              currentPick={currentPickNumber}
              myTeamId={teamId}
              autopickTeamIds={autoTeams}
              orderScrambled={orderScrambled}
            />
          </>
        }
        rosterRail={
          <RosterRail
            teams={railTeams}
            selectedTeamId={effectiveTeamId}
            onSelectTeam={setSelectedTeamId}
            slotOrder={rosterSlotOrder}
            slotAssignments={slotByPlayerId}
            playersById={playersById}
            rosterPlayerIds={rosterPlayerIds}
            biggestNeed={null}
          />
        }
        centerToolbar={
          <div className="flex items-center gap-3 px-4 py-3">
            {/*
              SAB-927: the start control, FIRST in the toolbar.

              In the toolbar rather than in the players-table empty state so that it
              survives the centre-view switch -- a commissioner sitting on BOARD must not
              lose the only way to start their draft. Rendered only for a commissioner of a
              pending league; resolveStartDraftGate decides both.
            */}
            {startGate.showControl && (
              <>
                <StartDraftControl
                  reason={startGate.reason}
                  starting={startingDraft}
                  onStart={handleStartDraft}
                />
                <span className="h-5 w-px flex-none bg-border" />
              </>
            )}
            {/*
              SAB-881: autodraft lives here, left of the view toggle, not in the rail.

              GATED ON "the draft is over", not on "the draft is running". It writes
              auto_draft_enabled, which is meaningless once a draft is finished -- but
              "only while running" is not achievable in THIS room: rule (e) routes
              in_progress and paused to the legacy room, so V2 only ever serves pending and
              completed. A running-only gate would mean the control never appears here at
              all. Phrasing it as hide-when-over also survives the eventual cutover of live
              drafting into V2 without another edit.

              Pending is the state that matters: setting autodraft and queueing players
              before the draft starts, then stepping away, is the whole scenario.
            */}
            {draftStatus !== 'completed' && (
              <>
                <AutoDraftControl
                  enabled={autoDraftOn}
                  saving={autoDraftSaving}
                  onChange={handleAutoDraftChange}
                />
                <span className="h-5 w-px flex-none bg-border" />
              </>
            )}
            {/* SAB-988: mute toggle. Shows a struck-through icon when muted AND when audio
                never unlocked, because both mean "you will hear nothing" -- and a control
                that looks live while the browser has silenced it is the worse of the two. */}
            {draftRunning && (
              <>
                <button
                  type="button"
                  data-testid="draft-cue-toggle"
                  aria-label={cue.muted ? 'Unmute draft sounds' : 'Mute draft sounds'}
                  aria-pressed={!cue.muted}
                  title={cue.muted
                    ? 'Draft sounds are muted'
                    : cue.armed
                      ? 'Draft sounds on — you will hear your pick and a 30s warning'
                      : 'Draft sounds on — tap anywhere to enable audio'}
                  onClick={cue.toggleMute}
                  className={cn(
                    'flex h-7 w-7 flex-none items-center justify-center rounded-lg border transition-colors',
                    cue.muted || !cue.armed
                      ? 'border-border text-muted-foreground hover:text-foreground'
                      : 'border-primary/40 bg-primary/10 text-primary',
                  )}
                >
                  {cue.muted || !cue.armed ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
                </button>
                <span className="h-5 w-px flex-none bg-border" />
              </>
            )}
            <CenterViewToggle view={centerView} onChange={onCenterView} />
            {/*
              SAB-932: the pool controls sit in the toolbar, NOT inside PlayersTable, so
              they survive the centre-view switch and so the filtering stays in one memo
              rather than being reimplemented per panel.
            */}
            {centerView === 'players' && draftStatus !== 'completed' && (
              <PoolControls
                positionFilters={positionFilters}
                  query={poolQuery}
                  onQueryChange={setPoolQuery}
                  posFilter={posFilter}
                  onPosFilterChange={setPosFilter}
                  hideDrafted={hideDrafted}
                  onHideDraftedChange={onHideDraftedChange}
                  hiddenDraftedCount={pool.hiddenDraftedCount}
                />
            )}
            {/*
              The player count is DROPPED once the draft is over. The pool is not fetched
              for a finished draft, so "0 players" was not a small number -- it was a false
              statement about the pool, sitting next to two true ones.
            */}
            <span className="text-[11.5px] text-muted-foreground">
              {draftStatus === 'completed'
                ? `${madePicks.length} picks · ${teams.length} teams`
                : `${tableRows.length} players · ${madePicks.length} picks · ${teams.length} teams`}
            </span>
          </div>
        }
        centerPanel={
          centerView === 'board' ? (
            <BoardPanel
              pickLogs={pickLogs}
              teams={boardTeams}
              totalRounds={totalRounds}
              currentPick={currentPickNumber}
              draftOrder={draftOrder}
              orderScrambled={orderScrambled}
            />
          ) : (
            <PlayersTable
              players={tableRows}
              loading={playersLoading || isLoading}
              draftedLabel={draftedLabel}
              cappedPositions={cappedPositions}
              queuedIds={new Set(queue)}
              onToggleQueue={toggleQueue}
              onDraft={handlePick}
              // The commissioner may also pick out of turn; handlePick sends
              // commissioner_pick so nfl_acquire_pick_lock accepts it.
              isMyTurn={isMyTurn || isCommissioner}
              drafting={isPicking}
              onPlayerClick={guessWho.active ? undefined : setDetailPlayerId}
              emptyState={playersEmptyState}
            />
          )
        }
        rightRail={
          <RightRail
            tab={railTab}
            onTabChange={onRailTab}
            queueCount={queue.length}
            unread={{ chat: 0, feed: 0 }}
            queue={queue.map(pid => {
              const p = availablePlayers.find(a => a.id === pid);
              return {
                playerId: pid,
                name: p?.full_name ?? `Queued player · ${pid.slice(0, 8)}`,
                position: p?.position ?? '—',
                projection: p?.proj_sab_pts ?? null,
              };
            })}
            onQueueMove={reorderQueue}
            onQueueRemove={removeFromQueue}
            onDraftFromQueue={handlePick}
            isMyTurn={isMyTurn}
            isPicking={isPicking}
            feed={feed}
            feedFilter={feedFilter}
            onFeedFilterChange={setFeedFilter}
            chatPanel={
              <DraftChatLive
                draftId={leagueId}
                teamName={currentTeam?.name ?? null}
                source={NFL_DRAFT_CHAT}
                resolveAuthorName={(msg: DraftChatMessage) =>
                  chatAuthorByUserId.get(msg.user_id) ?? 'Manager'}
              />
            }
          />
        }
      />

      {/* Reused wholesale: the panel owns card picking, rarity, targeting, the play call
          and the bomb-drop/explosion/screen-flash/sabotage-shake sequence. Everything it
          needs was already in scope on this page. */}
      {currentLeague && (
        <NFLDraftSabotagePanel
          open={sabotagePanelOpen}
          onOpenChange={setSabotagePanelOpen}
          leagueId={currentLeague.id}
          myTeamId={currentTeam?.id ?? null}
          myUserId={user?.id ?? null}
          teams={teams.map((t) => ({ id: t.id, name: t.name }))}
          currentRound={currentRound}
          onPlayed={() => { refetchActive(); }}
        />
      )}

        {/* SAB-931: player detail.
            MOUNTED ONLY WHEN OPEN. The legacy room mounts it unconditionally, and its
            closed-state effect still runs four setState calls -- two returning fresh
            identities -- on every render of the room. Cheaper and simpler to not mount it,
            and it removes a whole class of interference with the live-draft render path.
            detailPlayerId is the only thing that opens it, the room refuses to set it while
            masked, and the effect above clears it if a mask lands while it is open. */}
        {detailPlayerId && (
          <NFLPlayerDetailModal
            playerId={detailPlayerId}
            open
            onOpenChange={(open) => { if (!open) setDetailPlayerId(null); }}
          />
        )}
    </>
  );
}

/**
 * The provider is mounted HERE, by the page, not by the route.
 *
 * NFLLeagueProvider is not global -- NFLStaging wraps its own content in one
 * (NFLStaging.tsx:129). A route that renders this component bare inside ProtectedRoute
 * therefore has no NFLLeagueContext, and useNFLLeague throws on first render, which the
 * app's error boundary catches as "Something went wrong. An unexpected error occurred."
 *
 * That message is the same for every failure, so the symptom carried no hint that the cause
 * was a missing provider rather than bad data -- it read exactly like a page that could not
 * handle the league it was given. NFLDraftRoomV2.provider.test.tsx pins this by rendering
 * against the REAL context module; mocking useNFLLeague, as the sibling test file does,
 * cannot catch it.
 */
export default function NFLDraftRoomV2() {
  return (
    <NFLLeagueProvider>
      {/* This standalone wrapper is not routed today (NFLStaging renders the Content export
          directly and supplies its own tab-aware exit). A hard navigation is the honest
          fallback if it ever is: it genuinely leaves the room rather than pretending to. */}
      <NFLDraftRoomV2Content onExit={() => { window.location.href = '/nfl'; }} />
    </NFLLeagueProvider>
  );
}
